import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import FileDrop from '../../components/FileDrop';
import {
  LEGAL_LINKS,
  deriveAgreementType,
  agreementTypeLabel,
  type AgreementType,
  type SiteType,
} from './legalLinks';
import { documentLinkProps } from '../templateFile';
import apiService from '../../service/ApiService';
import '../initiation/PocTypePage.scss';

type LocationState = {
  pocId?: string;
  environment?: {
    siteType?: SiteType;
    testSiteAvailable?: boolean | 'NOT_NEEDED';
  };
  legal?: {
    agreementType: AgreementType;
    signedAgreementFileName: string | null;
    contractCoverFileName: string | null;
    declarationAccepted: boolean;
  };
  [key: string]: unknown;
};

const FILE_ACCEPT =
  '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const LegalAssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? {};
  const pocId = state.pocId;

  const siteType = state.environment?.siteType;
  const agreementType = deriveAgreementType(siteType ?? null);

  const [signedAgreementFileName, setSignedAgreementFileName] = useState<
    string | null
  >(state.legal?.signedAgreementFileName ?? null);
  const [contractCoverFileName, setContractCoverFileName] = useState<
    string | null
  >(state.legal?.contractCoverFileName ?? null);
  const [signedAgreementFile, setSignedAgreementFile] = useState<File | null>(
    null
  );
  const [contractCoverFile, setContractCoverFile] = useState<File | null>(null);
  const [declarationAccepted, setDeclarationAccepted] = useState(
    state.legal?.declarationAccepted ?? false
  );
  const [attempted, setAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const agreementLink =
    agreementType === 'TRI_PARTY_AGREEMENT'
      ? LEGAL_LINKS.triPartyAgreement
      : LEGAL_LINKS.pocAgreement;

  const errors = useMemo(() => {
    const next: Record<string, string> = {};
    if (!agreementType) {
      next.siteType =
        'PoC site type is missing. Go back to PoC site and choose Customer-facing or ISS-Internal.';
    }
    if (!signedAgreementFileName) {
      next.agreement = 'Upload the signed agreement before continuing.';
    }
    if (!contractCoverFileName) {
      next.cover = 'Upload the completed Contract Cover before continuing.';
    }
    if (!declarationAccepted) {
      next.declaration = 'Accept the declaration to continue.';
    }
    return next;
  }, [
    agreementType,
    signedAgreementFileName,
    contractCoverFileName,
    declarationAccepted,
  ]);

  const isValid = Object.keys(errors).length === 0;

  const legalPayload = agreementType
    ? {
        agreementType,
        signedAgreementFileName,
        contractCoverFileName,
        declarationAccepted,
      }
    : null;

    const handleNext = async () => {
      setAttempted(true);
      setSubmitError(null);
      if (!isValid || !legalPayload) return;
    
      if (!pocId) {
        setSubmitError('Missing PoC id. Complete Initiation first.');
        return;
      }
      if (!signedAgreementFile || !contractCoverFile) {
        setSubmitError('Both agreement and Contract Cover files are required.');
        return;
      }
    
      setIsSubmitting(true);
      try {
        const agreementDoc = await apiService.uploadDocument(
          pocId,
          'SIGNED_AGREEMENT',
          signedAgreementFile
        );
        const coverDoc = await apiService.uploadDocument(
          pocId,
          'CONTRACT_COVER',
          contractCoverFile
        );
    
        await apiService.saveLegal(pocId, {
          signedAgreementFileRef: agreementDoc.fileRef,
          contractCoverFileRef: coverDoc.fileRef,
          declarationAccepted: true,
        });
    
        navigate('/pocs/new/preparation/procurement', {
          state: {
            ...state,
            legal: {
              ...legalPayload,
              signedAgreementFileRef: agreementDoc.fileRef,
              contractCoverFileRef: coverDoc.fileRef,
            },
          },
        });
      } catch (err) {
        console.error(err);
        setSubmitError(
          'Could not save legal assessment. Check Network (2 uploads + PUT legal).'
        );
      } finally {
        setIsSubmitting(false);
      }
    };

  const onAgreementFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSignedAgreementFile(file);
    setSignedAgreementFileName(file ? file.name : null);
  };

  const onCoverFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setContractCoverFile(file);
    setContractCoverFileName(file ? file.name : null);
  };

  return (
    <div className="initiation">
      <aside className="initiation__phases" aria-label="PoC phases">
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">1</span>
          <span>Initiation</span>
        </div>
        <div className="initiation__phase initiation__phase--active">
          <span className="initiation__phase-num">2</span>
          <span>Preparation</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">3</span>
          <span>Running</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">4</span>
          <span>Evaluation</span>
        </div>
      </aside>

      <div className="initiation__main">
        <nav className="initiation__steps" aria-label="Preparation steps">
          <span className="initiation__step">Step 1</span>
          <span className="initiation__step">Step 2</span>
          <span className="initiation__step">Step 3</span>
          <span className="initiation__step">Step 4</span>
          <span className="initiation__step">Step 5</span>
          <span className="initiation__step initiation__step--active">
            Step 6
          </span>
          <span className="initiation__step">Step 7</span>
          <span className="initiation__step">Review and Submit</span>
        </nav>

        <Section theme="light" spacing="tight">
          <Eyebrow>POC Preparation</Eyebrow>
          <Headline as="h1" size="3xl">
            Legal assessment
          </Headline>
          <Lead>
            Step 6 — Download the correct agreement, complete and sign it with
            local legal support, upload the signed documents, and accept the
            declaration.
          </Lead>

          {agreementType ? (
            <p className="initiation__note">
              Based on your PoC site ({siteType === 'ISS_INTERNAL'
                ? 'ISS-Internal'
                : 'Customer-facing'}
              ), use the{' '}
              <strong>{agreementTypeLabel(agreementType)}</strong> — subject to
              adaptations solely required by any local laws.
            </p>
          ) : (
            <p className="bj-field__error" role="alert">
              {errors.siteType}
            </p>
          )}

          <ol className="phase-gate__list" style={{ listStyle: 'decimal' }}>
            <li>
              Please download the PoC agreement (link provided below) that is
              allocated to your country/group and populate it with support from
              your local legal representative.
            </li>
            <li>Get it signed by the authorized signatories in the country.</li>
            <li>Fill out the Contract Cover (link provided below).</li>
            <li>Upload the signed agreement(s) + Contract Cover.</li>
            <li>Accept the declaration.</li>
          </ol>

          <div className="phase-gate">
            <p className="phase-gate__title">Source of truth</p>
            <ul className="phase-gate__list">
              <li>
                <a className="scorecard__link" {...documentLinkProps(agreementLink.href)}>
                  {agreementLink.label}
                </a>
                {agreementLink.href === '#'
                  ? ' (placeholder URL)'
                  : ''}
              </li>
              <li>
                <a
                  className="scorecard__link"
                  {...documentLinkProps(LEGAL_LINKS.contractCover.href)}
                >
                  {LEGAL_LINKS.contractCover.label}
                </a>
                {LEGAL_LINKS.contractCover.href === '#'
                  ? ' (placeholder URL)'
                  : ''}
              </li>
              <li>
                <a
                  className="scorecard__link"
                  {...documentLinkProps(LEGAL_LINKS.responsibleLegalDirector.href)}
                >
                  {LEGAL_LINKS.responsibleLegalDirector.label}
                </a>
                {LEGAL_LINKS.responsibleLegalDirector.href === '#'
                  ? ' (placeholder URL)'
                  : ''}
              </li>
            </ul>
          </div>

          <label className="bj-field">
            <span className="bj-field__label">
              Upload signed {agreementType ? agreementTypeLabel(agreementType) : 'agreement'}{' '}
              (pdf/docx)
            </span>
            <FileDrop
              accept={FILE_ACCEPT}
              hint=".pdf or .docx, up to 10 MB"
              fileName={signedAgreementFileName}
              onChange={onAgreementFile}
            />
            {attempted && errors.agreement ? (
              <span className="bj-field__error">{errors.agreement}</span>
            ) : null}
          </label>

          <label className="bj-field">
            <span className="bj-field__label">
              Upload completed Contract Cover (pdf/docx)
            </span>
            <FileDrop
              accept={FILE_ACCEPT}
              hint=".pdf or .docx, up to 10 MB"
              fileName={contractCoverFileName}
              onChange={onCoverFile}
            />
            {attempted && errors.cover ? (
              <span className="bj-field__error">{errors.cover}</span>
            ) : null}
          </label>

          <label className="score-result__confirm">
            <input
              type="checkbox"
              checked={declarationAccepted}
              onChange={(event) => {
                setDeclarationAccepted(event.target.checked);
              }}
            />
            <span>
              I hereby declare that I have followed the recommended path and
              completed the required action points as outlined.
            </span>
          </label>
          {attempted && errors.declaration ? (
            <p className="bj-field__error">{errors.declaration}</p>
          ) : null}
          {submitError ? (
            <p className="bj-field__error">{submitError}</p>
          ) : null}

          <div className="initiation__actions">
            <button
              type="button"
              className="initiation__link-back"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() =>
                navigate('/pocs/new/preparation/it-security', {
                  state: {
                    ...state,
                    legal: legalPayload ?? state.legal,
                  },
                })
              }
            >
              ← Back to IT security
            </button>

            <button
              type="button"
              className="initiation__next"
              disabled={isSubmitting}
              onClick={handleNext}
            >
              {isSubmitting ? 'Saving...' : 'Next →'}
            </button>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default LegalAssessmentPage;
